/*
 * Dark Web Monitoring page.
 *
 * Headline stats (total breach mentions, emails breached, credential
 * leaks, email-only mentions), cumulative-exposure dual-bar chart and per-email
 * breach table.
 *
 * Calculations:
 *   - summary maps /getorgstats: totalBreaches=breachedUsers.allTotal,
 *     emailsBreached=breachedUsers.allUnique, credentialBreaches=passwords.withPass,
 *     emailOnlyBreaches=passwords.withoutPassOrHash, emailsScanned=assets.total.
 *   - byEmail maps /breachedusers rows (sorted by mentions DESC).
 *
 * Live data:
 *   - Summary stats + per-email breach rows are pulled from dwm-api
 *     (`useGetDwmOrgStatsQuery` / `useGetDwmBreachedUsersQuery`), both
 *     tenancy-scoped. Cached 6 hours (breach feeds refresh infrequently).
 *   - The dwm-api has no name/department on breach rows, so those columns
 *     are joined from tenancy user data (`useGetUsersQuery`) by email.
 *   - The cumulative exposure chart is backed by sp_GetBreachTrend via
 *     `useGetDwmBreachTrendQuery`, scoped to the shell's selected date range.
 *     The proc returns all-time daily data (cached per org in BreachService);
 *     the API filters to the range, rolls it up to an auto-chosen granularity,
 *     zero-fills gaps and echoes the granularity back. The browser only maps
 *     display labels; TrendChart still computes the cumulative running total.
 *   - The API carries no distinct-source count or latest-breach date per
 *     user, so those two columns show '-' until an endpoint provides them.
 *
 * Gated by FEATURES.darkWebMonitoring.
 */
import React, { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Grid, Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import { motion } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import SectionTitle from './SectionTitle';
import { useGetDwmOrgStatsQuery, useGetDwmBreachStatsQuery, useGetDwmBreachedUsersQuery, useGetDwmBreachTrendQuery } from '../../../store/services/dwm-service';
import { useGetUsersQuery } from '../../../store/services/user-service';

// The API returns a complete, zero-filled, chronologically ordered series for the
// selected range and echoes the granularity it chose (collation + padding happen
// server-side in BreachService), so the browser only formats a display label per
// bucket. Bucket keys: "YYYY-MM" (month), "YYYY-Www" (ISO week), "YYYY-MM-DD" (day).
function formatBucketLabel(bucket, granularity) {
    if (granularity === 'week') return bucket.slice(5); // "W07"
    if (granularity === 'day') {
        const d = new Date(`${bucket}T00:00:00Z`);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    const [y, m] = bucket.split('-').map(Number); // month
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

// The dwm-api breach rows carry no display name, so fall back to a readable
// name derived from the email local-part when the tenancy join misses.
function deriveNameFromEmail(email) {
    if (!email) return '';
    const local = String(email).split('@')[0];
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ') || email;
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Left-aligned "View" affordance for drillable rows: replaces whole-row
// click so the click target is explicit, matching the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

function StatCard({ label, value, sub, color, icon: Icon, loading }) {
    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {Icon && (
                    <Box aria-hidden="true" sx={{ position: 'absolute', top: 18, right: 18, display: 'flex', color: color || 'text.disabled', opacity: color ? 0.85 : 0.5 }}>
                        <Icon sx={{ fontSize: 22 }} />
                    </Box>
                )}
                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', pr: 4 }}>{label}</Typography>
                {/* Spinner while (re)fetching so a date change reads as "updating" rather
                    than silently showing the previous range's number. */}
                {loading ? (
                    <Box sx={{ height: 28, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={18} color="secondary" />
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, mt: 0.5, color: color || 'text.primary' }}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </Typography>
                )}
                {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
            </Box>
        </MainCard>
    );
}

// Cumulative breach-mention area chart (ApexCharts), styled to match the sims-page
// "Reporting Trend" card but with a single red series. Plots the running total of breach
// mentions within the selected period, starting at zero (no pre-window baseline) so a few
// mentions over a short window stay visible instead of being a flat blip.
function TrendChart({ data }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
    const labelColor = theme.palette.text.primary;

    // Running total within the selected range, from zero.
    const cum = useMemo(() => {
        let total = 0;
        return data.map((d) => {
            total += d.value;
            return { label: d.label, total };
        });
    }, [data]);

    // ApexCharts measures its parent once on mount; the card's height isn't settled until
    // after the fadeUp entrance animation, so force a re-measure on the next frame.
    useEffect(() => {
        const id = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        return () => cancelAnimationFrame(id);
    }, [data]);

    const options = {
        chart: { type: 'area', background: 'transparent', toolbar: { show: false }, animations: { enabled: false } },
        colors: ['#FF5252'],
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
            type: 'gradient',
            gradient: {
                type: 'vertical',
                colorStops: [
                    { offset: 0,   color: '#FF6B7A', opacity: 0.22 },
                    { offset: 50,  color: '#FF5252', opacity: 0.08 },
                    { offset: 100, color: '#FF5252', opacity: 0 },
                ],
            },
        },
        markers: {
            size: 0,
            hover: { size: 5 },
            colors: ['#FF5252'],
            strokeColors: theme.palette.background.paper,
            strokeWidth: 2,
            fillOpacity: 1,
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: cum.map((d) => d.label),
            labels: { style: { fontSize: '10px', fontFamily: 'Inter', colors: labelColor }, rotate: 0, hideOverlappingLabels: true },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { offsetX: -14, style: { fontSize: '10px', fontFamily: 'Inter', colors: labelColor } } },
        grid: { borderColor: gridColor, xaxis: { lines: { show: false } }, padding: { left: 4, right: 4, bottom: 0 } },
        legend: { show: false },
        tooltip: { theme: isDark ? 'dark' : 'light', style: { fontSize: '11px', fontFamily: 'Inter' } },
    };

    return (
        <Box sx={{ height: 240 }}>
            <ReactApexChart
                options={options}
                series={[{ name: 'Breach mentions', data: cum.map((d) => d.total), type: 'area' }]}
                type="area"
                height="100%"
                width="100%"
            />
        </Box>
    );
}

export default function DwmPage({ drill, startDate, endDate }) {

    // Resolve the active tenancy the same way the rest of the portal does:
    // explicit URL override, then the Redux tenancy slice, then the logged-in
    // user. Every dwm-api endpoint is tenancy-scoped so this must be present.
    const reduxTenancyId = useSelector((state) => state.tenancy.tenancyId);
    const tenancyId = useMemo(() => {
        const urlId = new URLSearchParams(window.location.search).get('tenancyId');
        if (urlId && urlId !== 'undefined') return parseInt(urlId, 10);
        if (reduxTenancyId) return parseInt(reduxTenancyId, 10);
        try { return JSON.parse(localStorage.getItem('user'))?.tenancyId; } catch { return undefined; }
    }, [reduxTenancyId]);

    const { data: orgStats, isLoading: statsLoading, isError: statsError } =
        useGetDwmOrgStatsQuery({ tenancyId }, { skip: !tenancyId });
    // Headline tiles - one call feeds all four. allTimeRows is the static all-time
    // figure (server-cached, same for every range); the rest are windowed to the
    // selected dates. isLoading gates tile 1 (first paint only, so the unchanging
    // all-time number never flickers on a date change); isFetching gates the period
    // tiles so they visibly refresh when the range changes.
    const { data: periodStats, isLoading: statsFirstLoad, isFetching: periodFetching } =
        useGetDwmBreachStatsQuery({ tenancyId, startDate, endDate }, { skip: !tenancyId });
    // isFetching (not isLoading) so the table spinner also shows on refetches when
    // the date range changes - otherwise the stale list lingers and looks unresponsive.
    const { data: breached, isFetching: breachedLoading, isError: breachedError } =
        useGetDwmBreachedUsersQuery({ tenancyId, page: 1, limit: 100, startDate, endDate }, { skip: !tenancyId });
    // Bucket size follows the selected span so short ranges don't collapse to a single
    // bar: day for up to ~6 weeks (e.g. 7d/30d), week up to ~5 months (e.g. 90d), month
    // beyond (6m/12m). This keeps the default 6-month view on months rather than weeks.
    const trendGranularity = useMemo(() => {
        if (!startDate || !endDate) return 'month';
        const days = Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1;
        if (days <= 45) return 'day';
        if (days <= 150) return 'week';
        return 'month';
    }, [startDate, endDate]);
    const { data: trendData, isLoading: trendLoading } =
        useGetDwmBreachTrendQuery({ tenancyId, startDate, endDate, granularity: trendGranularity }, { skip: !tenancyId });
    // Pulled once so breach rows can be joined to a name/department by email.
    const { data: tenancyUsers } = useGetUsersQuery(
        { tenancyId, perPage: 100000, page: 0, includes: '', query: '', extendedResults: true },
        { skip: !tenancyId }
    );

    const userMap = useMemo(() => {
        const m = new Map();
        for (const u of tenancyUsers?.data || []) {
            if (u?.email) m.set(String(u.email).toLowerCase(), u);
        }
        return m;
    }, [tenancyUsers]);

    // Headline numbers from /getorgstats. Credential vs email-only split comes
    // from the password analysis; emails scanned from the monitored asset count.
    const summary = useMemo(() => {
        const bu = orgStats?.breachedUsers || {};
        const pw = orgStats?.passwords || {};
        const assets = orgStats?.assets || {};
        return {
            totalBreaches: bu.allTotal ?? 0,
            emailsBreached: bu.allUnique ?? 0,
            credentialBreaches: pw.withPass ?? 0,
            emailOnlyBreaches: pw.withoutPassOrHash ?? 0,
            emailsScanned: assets.total ?? pw.total ?? 0,
        };
    }, [orgStats]);

    // Per-email rows from /breachedusers, joined to tenancy name/department.
    // mentions = total breach records; withCreds = records exposing a password.
    const byEmail = useMemo(() => {
        return (breached?.users || []).map((u) => {
            const match = userMap.get(String(u.email || '').toLowerCase());
            const joinedName = match ? `${match.firstName || ''} ${match.lastName || ''}`.trim() : '';
            return {
                email: u.email,
                name: joinedName || deriveNameFromEmail(u.email),
                dept: match?.department || '-',
                mentions: u.breaches ?? 0,
                withCreds: u.passwordAvailable ?? 0,
                // dwm-api has no distinct-source count. latestBreachAt is an ISO date
                // ("YYYY-MM-DD") or null; format it, dash when absent.
                sources: '-',
                latest: u.latestBreachAt
                    ? new Date(`${u.latestBreachAt}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                    : '-',
            };
        }).sort((a, b) => b.mentions - a.mentions);
    }, [breached, userMap]);


    // API already returns a complete ordered breach-mention series; just attach display
    // labels using the granularity the API chose.
    const trend = useMemo(() => {
        const gran = trendData?.granularity || 'month';
        return (trendData?.points || []).map((r) => ({
            key: r.bucket,
            label: formatBucketLabel(r.bucket, gran),
            value: Number(r.mentions) || 0
        }));
    }, [trendData]);

    // The breached-users table is the slow query (~10s), so it is NOT part of the
    // page-level gate - the summary and chart render immediately and the table shows
    // its own spinner and fills in when ready.
    const loading = statsLoading || trendLoading;
    const errored = statsError && breachedError;

    // The table is a top-N view (fetch capped at 100, render capped at 50), so its own
    // row count can't represent the total. Report the authoritative windowed totals from
    // the tiles instead - emails from tile 4, breach records from tile 2 - so the header
    // agrees with them; the rows below are just the top slice by mentions.
    const listShown = Math.min(byEmail.length, 50);
    const listEmails = periodStats?.distinctEmails ?? byEmail.length;
    const listMentions = periodStats?.totalMentions ?? byEmail.reduce((s, e) => s + e.mentions, 0);

    if (!tenancyId) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>No tenancy selected</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Select a tenancy to view dark web monitoring data.</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ p: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={28} color="secondary" />
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading dark web monitoring data...</Typography>
            </Box>
        );
    }

    if (errored) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Could not load dark web monitoring data</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>The monitoring service is unavailable or this tenancy has no breach feed configured.</Typography>
            </Box>
        );
    }

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <motion.div variants={fadeUp}>
                <Box sx={{ mb: 1 }}>
                    <SectionTitle>Dark web monitoring</SectionTitle>
                </Box>
            </motion.div>

            {/* Headline numbers */}
            <motion.div variants={fadeUp}>
                <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                    <Grid item xs={6} md={3}><StatCard label="Total breach mentions" value={periodStats?.allTimeRows ?? 0} loading={statsFirstLoad} color="#FF5252" sub="all time - across all sources" icon={WarningAmberOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Breach mentions in period" value={periodStats?.totalMentions ?? 0} loading={periodFetching} sub="within selected dates" icon={MailOutlineIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Plaintext password leaks" value={periodStats?.credMentions ?? 0} loading={periodFetching} color="#FF5252" sub="within selected dates" icon={LockOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Distinct emails breached" value={periodStats?.distinctEmails ?? 0} loading={periodFetching} sub="within selected dates" icon={AlternateEmailOutlinedIcon} /></Grid>
                </Grid>
            </motion.div>

            {/* Trend */}
            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Cumulative exposure
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                Running total of breached records - over the selected period - hover for detail
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: 'var(--red)', borderRadius: '2px' }} />
                                Breach mentions
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <TrendChart data={trend} />
                    </Box>
                </MainCard>
            </motion.div>

            {/* Breakdown by email */}
            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Most-mentioned emails
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                {breachedLoading
                                    ? 'Loading breached emails...'
                                    : `${listEmails} emails appear in ${listMentions} breach records${listEmails > listShown ? ` - showing top ${listShown} by mentions` : ''} - click a row to drill in`}
                            </Typography>
                        </Box>
                    </Box>
                    {breachedLoading ? (
                        <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                            <CircularProgress size={24} color="secondary" />
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Fetching breached emails - this can take a few seconds</Typography>
                        </Box>
                    ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <Box component="thead">
                                <Box component="tr">
                                    <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '1px' }} />
                                    {['Email', 'Name', 'Mentions', 'Credentials', 'Latest'].map((h) => (
                                        <Box key={h} component="th" sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', px: 2, py: 1.25, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>{h}</Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {byEmail.slice(0, 50).map((e) => (
                                    <Box component="tr" key={e.email}
                                        sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}>
                                        <Box component="td" sx={{ px: 2, py: 1.25, width: '1px', whiteSpace: 'nowrap' }}>
                                            <RowViewButton onClick={() => drill('user-detail', e.name, { type: 'user', value: e.email })} />
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.primary', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{e.email}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.name}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, fontWeight: 700, color: 'text.primary' }}>{e.mentions}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25 }}>
                                            {e.withCreds > 0 ? (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: '6px', py: '1px', borderRadius: '4px', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(255,82,82,.1)', color: '#E53935', border: '1px solid rgba(255,82,82,.25)' }}>
                                                    <LockOutlinedIcon sx={{ fontSize: 11 }} />
                                                    {e.withCreds} with password
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.disabled', fontSize: 11 }}>
                                                    <MailOutlineIcon sx={{ fontSize: 11 }} />
                                                    email only
                                                </Box>
                                            )}
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.disabled', fontSize: 11 }}>{e.latest}</Box>
                                    </Box>
                                ))}
                                {byEmail.length === 0 && (
                                    <Box component="tr"><Box component="td" colSpan={6} sx={{ px: 2, py: 4, textAlign: 'center', fontSize: 12, color: 'text.disabled' }}>No emails match this filter</Box></Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    )}
                </MainCard>
            </motion.div>
        </motion.div>
    );
}
